import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { DesktopNav } from './header/DesktopNav';
import { MobileMenu } from './header/MobileMenu';
import { LangToggle } from './LangToggle';

const HeaderActions = () => (
  <div className="hidden lg:flex items-center gap-3">
    <LangToggle />
    <Link
      to="/console"
      data-testid="header-console-signin"
      className="text-[#CBD5E1] hover:text-white text-sm font-medium px-4 py-2.5 rounded-lg transition-colors duration-200"
    >
      Sign In
    </Link>
    <Link
      to="/cloud-access"
      data-testid="header-request-access"
      className="bg-[#F29B3D] hover:bg-[#DD8226] text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors duration-200"
    >
      Request Cloud Access
    </Link>
  </div>
);

const HeaderLogo = () => (
  <Link to="/" data-testid="logo-link" className="flex flex-col items-start group relative z-10 shrink-0">
    <img src="/daakyi-logo-nav.png" alt="DAAKYI Cloud" className="h-8 lg:h-9 transition-opacity duration-200 group-hover:opacity-85" />
  </Link>
);

const useHeaderState = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const timeoutRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    document.body.dataset.menuOpen = isMobileMenuOpen ? 'true' : 'false';
    return () => { document.body.style.overflow = ''; document.body.dataset.menuOpen = 'false'; };
  }, [isMobileMenuOpen]);

  const handleMouseEnter = (dropdown) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setActiveDropdown(dropdown);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setActiveDropdown(null), 200);
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return { isScrolled, isMobileMenuOpen, setIsMobileMenuOpen, activeDropdown, handleMouseEnter, handleMouseLeave, isActive };
};

export const Header = () => {
  const { isScrolled, isMobileMenuOpen, setIsMobileMenuOpen, activeDropdown, handleMouseEnter, handleMouseLeave, isActive } = useHeaderState();

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 w-full z-50 bg-[#120836] transition-shadow duration-300 ${
        isScrolled ? 'shadow-[0_2px_16px_rgba(3,10,20,0.4)] border-b border-white/[0.06]' : 'border-b border-white/[0.04]'
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 relative z-50">
        <div className={`flex items-center justify-between transition-[height] duration-300 ${isScrolled ? 'h-[56px] lg:h-[60px]' : 'h-[68px] lg:h-[72px]'}`}>
          <HeaderLogo />

          <DesktopNav
            activeDropdown={activeDropdown}
            handleMouseEnter={handleMouseEnter}
            handleMouseLeave={handleMouseLeave}
            isActive={isActive}
          />

          <HeaderActions />

          <div className="flex items-center gap-2 lg:hidden">
            <LangToggle />
            <button
              data-testid="mobile-menu-toggle"
              className="p-3 text-[#CBD5E1] hover:text-white rounded-lg hover:bg-white/[0.06] transition-all"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      <MobileMenu
        isMobileMenuOpen={isMobileMenuOpen}
        setIsMobileMenuOpen={setIsMobileMenuOpen}
        isActive={isActive}
      />
    </header>
  );
};

export default Header;
